import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../store';
import MyWorkCard from './MyWorkCard';
import Expander from './Expander';
import { displayName } from '../people';
import { resolveMentions } from '../mentions';
import { MentionInput } from './MentionInput';
import { IssueTextInline } from './IssueText';
import { myWork, isMyWork, iAmAccountableFor, type MyCard } from '../my-work';
import { rollUpWorkspaceEpics, type BoardTier, type EpicHealth } from '../epic-rollup';
import { epicBackground } from '../epics';
import { MONTHS } from '../when';
import { steadyNow } from '../steady-now';
import {
  icon as notifIcon,
  line as notifLine,
  snippet as notifSnippet,
  meta as notifMeta,
  isUnread,
  isHandled,
  newestFirst,
  needsAction as isNeedsAction,
  leadsToSignoffs,
  type Notification,
} from '../notifications';

/**
 * MY WORK — the personal, cross-board hub (P2, #457). One nav row, TWO tabs:
 *
 *   • TO DO  — everything assigned to the signed-in person across every board,
 *     bucketed to ACT on (needs-you-first / doing-now / up-next), with a rail of
 *     the epics they own and the areas their work sits in.
 *   • INBOX  — the messaging centre: everything DIRECTED at them, cross-board —
 *     the in-app home of the notification feed, split into what needs their
 *     action (sign-offs awaiting them, questions aimed at them) and what is FYI
 *     (decisions, replies, status updates). The bell becomes a quick peek; this
 *     is where you read and act. Distinct from Activity (one board, everyone's
 *     events, a passive audit).
 *
 * For EVERYONE, not just managers: a read over the same cross-board cards the
 * Portfolio loads (store.portfolioIssues) and the person's own notification feed
 * (store.notifications), which is already gathered from every board they are on.
 * Opening anything switches to its board. Built to dev-preview/mywork-detailed/.
 */

const HEALTH_WORD: Record<EpicHealth, string> = { ontrack: 'On track', atrisk: 'At risk', offtrack: 'Off track' };
const HEALTH_CLS: Record<EpicHealth, string> = { ontrack: 'g', atrisk: 'a', offtrack: 'r' };

/* The two halves of the inbox read from `needsAction` in notifications.ts — the
   one place that decides which kinds want a response (a sign-off waiting on my
   signature, a question aimed at me, a cover handed to me) versus which are FYI
   (decisions, replies, status updates, cover arranged for me). The count that
   badges the Inbox tab and the "My work" nav row is the UNREAD needs-action
   items — "how many things are waiting on me" — so both read the same rule. */

/* A blocked week is a different shape of week, so the rail swaps its second card
   from "My areas" to "Blocked, by reason" once this many of my cards are blocked —
   the column always carries the most useful cut for the moment. */
const BLOCKED_HEAVY = 3;

/** An ISO 'YYYY-MM-DD' to "4 Oct", read off the parts so no timezone shifts it. */
function fmtDue(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
}
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The day in words — "Tuesday 6 Oct" — the one orienting line at the top of My
 *  Work (#3). The top bar already titles the page "My work", so the masthead no
 *  longer repeats it; this states the day instead. MONTHS is the worded list
 *  (R16: "Sept", not "Sep"). */
function wordedDay(now: number): string {
  const d = new Date(now);
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The make-up of a bucket, read as its own sub-line — "1 overdue · 2 blocked".
 *  Only non-zero signals show; when none do, a calm fallback. Each signal appears
 *  under exactly one bucket, so the number and what composes it read together and
 *  nothing is restated across the masthead (#3, option C). */
function signalLine(parts: [n: number, word: string][], fallback: string): string {
  const shown = parts.filter(([n]) => n > 0).map(([n, word]) => `${n} ${word}`);
  return shown.length ? shown.join(' · ') : fallback;
}

export default function MyWork() {
  const cardsByBoard = useApp((s) => s.portfolioIssues);
  const areasByBoard = useApp((s) => s.portfolioAreas);
  const projects = useApp((s) => s.projects);
  const myEmail = useApp((s) => s.user?.email ?? '');
  const loading = useApp((s) => s.portfolioLoading);
  const loadPortfolio = useApp((s) => s.loadPortfolio);
  const livePortfolioIssues = useApp((s) => s.livePortfolioIssues);
  const setProject = useApp((s) => s.setProject);
  /* drillTo, not setView: opening anything from My Work plants a "← Back to My
     work" trail (cameFrom), so the round trip the mock drew works. setView would
     clear cameFrom and strand the return. */
  const drillTo = useApp((s) => s.drillTo);
  const openIssue = useApp((s) => s.openIssue);
  const notifications = useApp((s) => s.notifications);
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);
  const markNotificationDone = useApp((s) => s.markNotificationDone);
  const markNotificationActive = useApp((s) => s.markNotificationActive);
  const replyFromInbox = useApp((s) => s.replyFromInbox);
  const showFlash = useApp((s) => s.showFlash);
  const emails = useApp((s) => s.access.emails);
  // The inline card panel (#2) — opening a card HERE rather than switching board.
  const openCardInline = useApp((s) => s.openCardInline);
  const closeCardInline = useApp((s) => s.closeCardInline);
  const inlineCardBoard = useApp((s) => s.inlineCardBoard);
  const inlineCardId = useApp((s) => s.inlineCardId);

  // The tab lives on the store (not component-local) so leaving for a card and
  // coming back via "Back to My work" returns to the tab you left — the Inbox,
  // not always To do (#5).
  const tab = useApp((s) => s.myworkTab);
  const setTab = useApp((s) => s.setMyworkTab);
  /* The one inbox row whose reply box is open, the draft in it, and what went
     wrong if a send failed — replying happens IN PLACE, so at most one row is
     expanded at a time (opening another closes the first). */
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyErr, setReplyErr] = useState('');
  const [sending, setSending] = useState(false);
  /* The long tail — "Up next" is often dozens of not-started cards. Show a handful
     and keep the rest one click away, so the screen stays one screen tall. */
  const [showAllUpNext, setShowAllUpNext] = useState(false);
  const UP_NEXT_PEEK = 5;
  /* #3 — which blocker reason is drilled open in the rail. Clicking a reason
     reveals the card(s) waiting on it IN PLACE (the workstation idea: don't leave
     the screen to find out what a reason covers); clicking a revealed card opens
     it. At most one reason open at a time. */
  const [openReason, setOpenReason] = useState<string | null>(null);
  /* #1 — whether the handled "Done" group is revealed. Handled call-outs (replied
     to, or marked done) leave the active inbox and collect here, so you can still
     find and reopen them without leaving the screen. */
  const [showDone, setShowDone] = useState(false);
  /* #1/#2 — which inbox rows are fading out (replied-to or marked done). A row
     fades and collapses before it leaves the active list for the Done reveal, so
     dealing with a call-out reads as a gentle departure, not a sudden vanish. */
  const [leavingIds, setLeavingIds] = useState<Set<string>>(new Set());
  const leaveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /* #2 — the inline card panel's leaving animation, run here (the BudgetPay
     precedent): on close we keep the card mounted for the slide-out, then clear
     the store. The board name is held so the footer still reads during the exit. */
  const [panelLeaving, setPanelLeaving] = useState(false);
  const [panelBoardName, setPanelBoardName] = useState('');
  const panelTimer = useRef<ReturnType<typeof setTimeout>>();
  const PANEL_MS = 260;

  // HELD STILL WITHIN THE MINUTE, so the memos below that take `now` as a
  // dependency actually cache. `Date.now()` here was new on every render, so
  // every roll-up over every card re-ran every time. See src/steady-now.ts.
  const now = steadyNow();
  const me = myEmail.trim().toLowerCase();

  // Same load the Portfolio and Insights use — reads every board the person can
  // see. Trigger it on arrival if nothing is loaded yet, exactly as Insights does
  // when it opens on "all boards".
  useEffect(() => {
    if (cardsByBoard.size === 0 && !loading && projects.length > 0) void loadPortfolio();
  }, [cardsByBoard.size, loading, projects.length, loadPortfolio]);

  // Keep the cross-board cards LIVE while this screen is open, so assigning a card
  // to yourself or a status change on any board shows at once — no reload. The
  // tenant-wide listener is dropped on unmount: a scoped cost, only while someone
  // is looking. The Inbox is already live (the notification feed streams in).
  useEffect(() => livePortfolioIssues(), [livePortfolioIssues]);

  const boardName = useMemo(() => {
    const m = new Map(projects.map((p) => [p.id, p.name]));
    return (id: string) => m.get(id) ?? '—';
  }, [projects]);

  // MY WORK = what I'm DOING (the doer's list). A SECOND read, "I'm accountable
  // for", is the work I OWN that someone else is doing — the two-part assignee
  // split (A). They never double-count: a card I own with no doer yet is in my
  // doing-list, not the accountable one.
  const work = useMemo(() => myWork(cardsByBoard, boardName, myEmail, now), [cardsByBoard, boardName, myEmail, now]);
  const accountable = useMemo(
    () => myWork(cardsByBoard, boardName, myEmail, now, iAmAccountableFor),
    [cardsByBoard, boardName, myEmail, now],
  );

  // My epics — the ones I OWN (plurality owner across their cards) OR am WORKING
  // IN (hold an open card assigned to me), via the same workspace roll-up the
  // Portfolio uses (Vinnie 6 Oct: an epic you work in but don't lead was hidden —
  // on a board where your cards are few you owned none, so none showed). The
  // lifecycle tier is irrelevant here (no banding), so every board is handed the
  // same placeholder tier. Owned ones carry an "own" marker.
  const myEpics = useMemo(() => {
    const tiers: BoardTier[] = projects.map((p) => ({ id: p.id, name: p.name, tier: 'indelivery' }));
    const ws = rollUpWorkspaceEpics(cardsByBoard, tiers, now);
    const allNames = ws.epics.map((e) => e.name);
    // The epics I have an open card in — the "working in" half.
    const workingIn = new Set<string>();
    for (const [, cards] of cardsByBoard) {
      for (const c of cards) {
        if (!isMyWork(c, me)) continue;
        if (c.status === 'done' || c.status === 'wontdo') continue;
        if (c.epic) workingIn.add(c.epic);
      }
    }
    return ws.epics
      .map((e) => ({ e, own: e.owner.trim().toLowerCase() === me }))
      .filter(({ e, own }) => own || workingIn.has(e.name))
      .map(({ e, own }) => ({
        name: e.name,
        colour: epicBackground(e.name, allNames),
        progressPct: e.progressPct,
        health: e.health,
        own,
      }))
      // Owned first, then the ones I only work in; each group alphabetical.
      .sort((a, b) => Number(b.own) - Number(a.own) || a.name.localeCompare(b.name));
  }, [cardsByBoard, projects, me, now]);

  // My areas — where my work sits: my cards grouped by area name, with how many
  // are open and how far along I am in each.
  const myAreas = useMemo(() => {
    const map = new Map<string, { name: string; colour: string; open: number; total: number; done: number }>();
    for (const [boardId, cards] of cardsByBoard) {
      const areaById = new Map((areasByBoard.get(boardId) ?? []).map((a) => [a.id, a]));
      for (const c of cards) {
        if (!isMyWork(c, me)) continue;
        const area = c.area ? areaById.get(c.area) : undefined;
        if (!area) continue;
        let row = map.get(area.name);
        if (!row) map.set(area.name, (row = { name: area.name, colour: area.colour || '', open: 0, total: 0, done: 0 }));
        row.total++;
        if (c.status === 'done') row.done++;
        if (c.status !== 'done' && c.status !== 'wontdo') row.open++;
      }
    }
    return [...map.values()].sort((a, b) => b.open - a.open || a.name.localeCompare(b.name));
  }, [cardsByBoard, areasByBoard, me]);

  // "Blocked, by reason" — the rail's heavy-week swap: my blocked cards tallied by
  // what each is waiting on, so the slot answers "what do I chase to unblock?".
  // Each reason carries the cards behind it, so clicking it can reveal them (#3).
  const blockedByReason = useMemo(() => {
    const map = new Map<string, MyCard[]>();
    for (const c of work.needsFirst) {
      if (!c.blocker) continue;
      const list = map.get(c.blocker);
      if (list) list.push(c);
      else map.set(c.blocker, [c]);
    }
    return [...map.entries()]
      .map(([reason, cards]) => ({ reason, n: cards.length, cards }))
      .sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason));
  }, [work.needsFirst]);
  const heavyWeek = work.glance.blocked >= BLOCKED_HEAVY && blockedByReason.length > 0;

  // The inbox, from the person's own cross-board notification feed. Split by
  // whether it needs their action; newest first within each half; the badge is the
  // unread needs-action count.
  const nameOf = (email: string) => displayName(email, profiles);
  // A handled call-out (replied to, or marked done) leaves the active inbox and
  // moves to the "Done" reveal below — it stays a read row in the centre, so it is
  // kept in history, out of the way (#1). Unhandled splits the usual two ways.
  const { needsAction, fyi, done, needsActionCount } = useMemo(() => {
    const live = notifications.filter((n) => !isHandled(n));
    const na = newestFirst(live.filter(isNeedsAction));
    const fy = newestFirst(live.filter((n) => !isNeedsAction(n)));
    const dn = newestFirst(notifications.filter(isHandled));
    // The count IS the "Needs my action" set — every unhandled call-out that
    // wants an action, read or not (#2). Keying it off unread let a read-but-
    // unactioned row sit in the section while the masthead said "all caught up".
    return { needsAction: na, fyi: fy, done: dn, needsActionCount: na.length };
  }, [notifications]);

  // #2 — open a card in the inline panel, on whatever board it lives, WITHOUT
  // switching the app to that board (no setProject, so none of the board-load
  // cost). The person stays on My Work; the panel is the workstation.
  const openCard = (boardId: string, issueId: string) => {
    clearTimeout(panelTimer.current);
    panelTimer.current = undefined;
    setPanelLeaving(false);
    setPanelBoardName(boardName(boardId));
    openCardInline(boardId, issueId);
  };
  const openCardFromRow = (m: MyCard) => openCard(m.boardId, m.issue.id);
  // Close with the slide-out, then clear the store once it has left (reduced
  // motion closes at once). Mirrors the budget drawer's state machine.
  const closePanel = useCallback(() => {
    const quick = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (quick) {
      closeCardInline();
      return;
    }
    if (panelTimer.current) return;
    setPanelLeaving(true);
    panelTimer.current = setTimeout(() => {
      panelTimer.current = undefined;
      setPanelLeaving(false);
      closeCardInline();
    }, PANEL_MS);
  }, [closeCardInline]);
  useEffect(() => () => clearTimeout(panelTimer.current), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { for (const t of leaveTimers.current.values()) clearTimeout(t); }, []);
  // The escape hatch: leave for the full card on its own board, planting the
  // "← Back to My work" trail so the round trip returns here.
  const openFullCard = (boardId: string, issueId: string) => {
    closeCardInline();
    setPanelLeaving(false);
    clearTimeout(panelTimer.current);
    panelTimer.current = undefined;
    setProject(boardId);
    drillTo('board');
    openIssue(issueId);
  };

  // A handled call-out FADES OUT of the active inbox rather than vanishing (Vinnie
  // 6 Oct: "instead of it suddenly disappearing can it fade out gently"). The row
  // is marked leaving — it fades and collapses — and only once that has played does
  // markNotificationDone move it to the Done reveal. Reduced motion snaps straight
  // to handled. Timers are per-row so several can leave at once; cleared on unmount.
  // Slower than a plain fade (Vinnie #4): the row first flashes the done-green —
  // the same tint a handled row wears — holds a beat so the "done" reads, then
  // fades and collapses. The CSS @keyframes mw-ib-leave owns the look; this must
  // match its total duration so the row is marked handled exactly as it finishes.
  const IB_LEAVE_MS = 900;
  const fadeThenDone = (n: Notification) => {
    const quick = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (quick) {
      void markNotificationDone(n.id);
      return;
    }
    if (leaveTimers.current.has(n.id)) return;
    setLeavingIds((s) => new Set(s).add(n.id));
    const t = setTimeout(() => {
      leaveTimers.current.delete(n.id);
      void markNotificationDone(n.id);
      setLeavingIds((s) => {
        const next = new Set(s);
        next.delete(n.id);
        return next;
      });
    }, IB_LEAVE_MS);
    leaveTimers.current.set(n.id, t);
  };

  // #1 — mark an inbox call-out done: it is handled, so it LEAVES the active inbox
  // (kept in the centre and the "Done" reveal below, stamped as handled) and says
  // so. Replying does the same on success — both mean "I've dealt with this".
  const markDone = (n: Notification) => {
    showFlash('Marked done');
    fadeThenDone(n);
  };

  // Undo a mark-done (#3): the call-out returns to "Needs my action". Marking done
  // is no longer a one-way move — the Done row carries this instead of a dead ✓
  // that looked like the Mark-done button but did nothing.
  const markActive = (n: Notification) => {
    showFlash('Moved back to your inbox');
    void markNotificationActive(n.id);
  };

  // Open a notification's target. A call-out ON A CARD (a mention or a reply)
  // opens the card in the inline panel — the workstation, same as an agenda row,
  // no board switch (#2). The board-level targets (a sprint, a release, a
  // document) still drill to their screen, planting "← Back to My work" so the
  // round trip returns here — the inbox is a screen you came FROM.
  const goTo = (n: Notification) => {
    // A call-out ON A CARD opens the card in the inline panel — the workstation,
    // no board switch. Route by what it POINTS AT (targetKind 'issue'), not its
    // kind, so a mention and a reply open the card as before AND a status posted on
    // a card and a block raised on a card do too — these were falling through to the
    // Documents list. A sign-off is excluded here: it leads to the Sign-offs screen
    // below, even though its paper may live on a card. If the card can't be resolved
    // (a stale notification whose card was since deleted → no targetId/boardId), SAY
    // SO rather than dropping the person on a bare board with nothing open.
    if (n.targetKind === 'issue' && !leadsToSignoffs(n)) {
      if (n.boardId && n.targetId) openCard(n.boardId, n.targetId);
      else showFlash('That card is no longer available.');
      return;
    }
    if (n.boardId && n.boardId !== projectId) setProject(n.boardId);
    // A sign-off or cover call-out leads to the Sign-offs screen, where the pending
    // signature actually lives — not the generic Documents list, which holds no
    // sign-offs and so read as a dead end ("go sign this" → an empty page).
    if (leadsToSignoffs(n)) {
      drillTo('approvals');
    } else if (n.targetKind === 'sprint') {
      drillTo('sprints');
    } else if (n.targetKind === 'release') {
      drillTo('releases');
    } else {
      drillTo('documents');
    }
  };

  const dueMark = (m: MyCard): { text: string; warn: boolean } => {
    if (m.overdue && m.issue.dueDate) return { text: `${fmtDue(m.issue.dueDate)} · ${-m.dueDays!}d over`, warn: true };
    if (m.dueDays === 0 && m.issue.dueDate) return { text: 'due today', warn: true };
    if (m.issue.dueDate) return { text: `due ${fmtDue(m.issue.dueDate)}`, warn: false };
    return { text: '', warn: false };
  };

  // One agenda row — box-free, hairline-separated. The ref sits on its own line
  // and stays vertically centred; the blocker reason drops BELOW the title (so the
  // "what to chase" reads as a second line, not a crowded tail).
  const agendaRow = (m: MyCard) => {
    const mark = dueMark(m);
    return (
      <button type="button" key={`${m.boardId}:${m.issue.id}`} className="arow" onClick={() => openCardFromRow(m)}>
        <span className="arow-dot" style={{ background: `var(--s-${m.issue.status})` }} aria-hidden="true" />
        <span className="arow-ref">{m.issue.ref}</span>
        <span className="arow-main">
          <span className="arow-t" title={m.issue.title} aria-label={m.issue.title}>{m.issue.title}</span>
          {m.blocker ? (
            <span className="arow-blk">
              <span className="arow-blk-tag">Blocked</span>
              {m.blocker}
            </span>
          ) : null}
        </span>
        <span className="arow-board">{m.boardName}</span>
        {mark.text ? (
          <span className={`arow-due${mark.warn ? ' is-warn' : ''}`}>{mark.text}</span>
        ) : (
          <span className="arow-due is-none" aria-hidden="true" />
        )}
      </button>
    );
  };

  // A typographic section band (bold title + count + a note), then its rows — no
  // box. `attn` tints the rule for the "needs you first" band. `tone`, when given,
  // is the section's colour (#4): a dot before the title and the count take it, so
  // the band ties back to its matching tile in the masthead glance.
  const agendaSection = (title: string, note: string, rows: ReactNode, count: number, attn = false, tone?: string) => (
    <section className="asec">
      <div className={`asec-h${attn ? ' attn' : ''}`}>
        {tone && <span className="asec-dot" style={{ background: tone }} aria-hidden="true" />}
        <span className="asec-t">{title}</span>
        <span className="asec-n" style={tone ? { color: tone } : undefined}>{count}</span>
        <span className="asec-note">{note}</span>
      </div>
      {rows}
    </section>
  );

  // Open the reply box on one row (closing any other), discarding an old draft.
  const openReply = (n: Notification) => {
    setReplyFor(n.id);
    setReplyText('');
    setReplyErr('');
  };
  const cancelReply = () => {
    setReplyFor(null);
    setReplyText('');
    setReplyErr('');
  };
  // Post the reply to the card on ITS board, in place; on success mark the
  // call-out done and collapse. A failure stays open with the reason, so nothing
  // the person typed is lost.
  const sendReply = async (n: Notification) => {
    if (sending) return;
    if (!replyText.trim()) return;
    if (!n.boardId || !n.targetId) {
      setReplyErr('This message has no card to reply on.');
      return;
    }
    setSending(true);
    setReplyErr('');
    // Turn the @names shown while typing back into the stored @addresses, exactly
    // as the card composer does — so a mention in a reply survives a rename and
    // reaches the person it points at (the board's fan-out reads the address).
    // Case-insensitive, like the assignee/owner compares above: a capitalised
    // copy of your own address must not survive as a mention target, or a reply
    // could @-mention you back to yourself (F11, the identity match-casing seam).
    const others = emails.filter((e) => e.toLowerCase() !== me);
    const body = resolveMentions(replyText.trim(), others, profiles);
    const problem = await replyFromInbox(n.boardId, n.targetId, myEmail, body);
    setSending(false);
    if (problem) {
      setReplyErr(problem);
      return;
    }
    // Confirm it sent AND landed: the reply posts as a comment on the card, so say
    // where. The row also collapses and is marked read, but that change is quiet —
    // this is the unmistakable "it worked" (Vinnie 6 Oct: the inbox reply gave no
    // sign it had been sent or posted).
    showFlash(n.targetRef ? `Reply posted to ${n.targetRef}` : 'Reply posted');
    // Replying deals with the call-out — mark it handled so it leaves the inbox
    // (kept in the centre and the Done reveal), not merely read (#1). The row
    // fades out gently rather than vanishing (Vinnie 6 Oct) — fadeThenDone runs
    // the exit, then marks it handled; close the reply box first so it collapses
    // with the row.
    cancelReply();
    fadeThenDone(n);
  };

  // One inbox row. "Needs my action" rows carry the act button — Sign on a
  // sign-off, Reply (which opens a reply box in place) on a mention; every row
  // can be opened on its board or marked done. Unread shows as a dot + weight,
  // never a coloured rail (R12).
  // `handled` renders the row in the "Done" reveal: muted green, read-only but for
  // Open, no act buttons or unread dot — it has already been dealt with (#1).
  const inboxRow = (n: Notification, handled = false) => {
    const ic = notifIcon(n);
    const parts = notifLine(n, nameOf);
    const sub = notifSnippet(n, nameOf);
    const canReply = !handled && n.kind === 'mention' && !!n.targetId;
    const canSign = !handled && n.kind === 'signoff_requested';
    const replying = !handled && replyFor === n.id;
    const leaving = leavingIds.has(n.id);
    return (
      <div className={`mw-ib-item${leaving ? ' is-leaving' : ''}`} key={n.id}>
        <div className={`mw-ib-row${!handled && isUnread(n) ? ' is-unread' : ''}${handled ? ' is-done' : ''}`}>
          <span className={`np-ic ${ic.cls}`} aria-hidden="true">{ic.glyph}</span>
          <div className="mw-ib-main">
            <p className="mw-ib-line">
              {parts.map((p, i) => (p.b ? <b key={i}>{p.t}</b> : <span key={i}>{p.t}</span>))}
            </p>
            {sub && (
              <p className="mw-ib-snip">
                {/* Through the same renderer the card feed uses, so an @name in the
                    quoted comment reads as "@Pat Moore" tinted — and your own name
                    stands out — not the raw stored address. Inline, because the
                    snippet lives in a <p>: no block list wrappers, just the tinted
                    one-line preview. */}
                <IssueTextInline text={sub} mentions={me} />
              </p>
            )}
            <p className="mw-ib-meta">{notifMeta(n)}</p>
          </div>
          <div className="mw-ib-actions">
            {canSign && (
              <button type="button" className="btn btn-primary btn-small" onClick={() => goTo(n)}>
                Sign
              </button>
            )}
            {canReply && (
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={() => (replying ? cancelReply() : openReply(n))}
              >
                Reply
              </button>
            )}
            <button type="button" className="btn btn-small" onClick={() => goTo(n)}>
              Open
            </button>
            {handled ? (
              // Not a dead ✓ in the Mark-done slot (which read as a broken toggle):
              // an explicit Undo that moves the call-out back to Needs my action.
              // The row's green tint + the "Done" heading already say it's handled.
              <button
                type="button"
                className="btn btn-small mw-ib-undo"
                title="Move back to Needs you first"
                aria-label="Undo — move back to Needs you first"
                onClick={() => markActive(n)}
              >
                Undo
              </button>
            ) : (
              <button
                type="button"
                className="mw-ib-done"
                title="Mark done"
                aria-label="Mark done"
                onClick={() => markDone(n)}
              >
                ✓
              </button>
            )}
          </div>
          {!handled && isUnread(n) && <span className="mw-ib-dot" aria-hidden="true" />}
        </div>
        {replying && (
          <div className="mw-ib-reply">
            <p className="mw-ib-reply-to">
              Reply to <b>{nameOf(n.actor)}</b>
              {n.targetRef ? <> on <b>{n.targetRef}</b></> : null} — posts as a comment on the card
            </p>
            {/* The same mention-aware field the card feed uses, so "@ to mention
                someone" works here exactly as it does on a card — the picker, Tab
                or Enter to complete, Enter to send, Escape to step out. */}
            <MentionInput
              multiline
              className="input mw-ib-reply-box"
              value={replyText}
              onChange={setReplyText}
              onSubmit={() => void sendReply(n)}
              onCancel={cancelReply}
              placeholder="Write a reply…  @ to mention someone"
              ariaLabel="Write a reply"
              autoFocus
            />
            {replyErr && <p className="mw-ib-reply-err">{replyErr}</p>}
            <div className="mw-ib-reply-actions">
              <button
                type="button"
                className="btn btn-primary btn-small"
                disabled={sending || !replyText.trim()}
                onClick={() => void sendReply(n)}
              >
                {sending ? 'Sending…' : 'Send reply'}
              </button>
              <button type="button" className="btn btn-small" onClick={cancelReply}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const toDoEmpty = !loading && work.total === 0;
  const upNextShown = showAllUpNext ? work.upNext : work.upNext.slice(0, UP_NEXT_PEEK);

  // The inbox masthead sentence — same idea as the to-do one, about what's waiting.
  const inboxSit: ReactNode[] = (() => {
    const unreadFyi = fyi.filter(isUnread).length;
    if (needsActionCount > 0) {
      const out: ReactNode[] = [
        <b key="n">{needsActionCount}</b>,
        needsActionCount === 1 ? ' thing needs your action.' : ' things need your action.',
      ];
      out.push(
        unreadFyi > 0 ? (
          <span key="t"> <b>{unreadFyi}</b> more to read.</span>
        ) : (
          <span key="t" className="calm"> Nothing else waiting.</span>
        )
      );
      return out;
    }
    if (unreadFyi > 0) {
      return [
        <b key="n">{unreadFyi}</b>,
        unreadFyi === 1 ? ' update to read.' : ' updates to read.',
        <span key="c" className="calm"> Nothing needs your action.</span>,
      ];
    }
    return [<span key="c" className="calm">You’re all caught up — nothing waiting.</span>];
  })();

  /* #4 — the masthead glance now mirrors the THREE sections below, one tile each,
     in the SAME colour the section header carries, so the number at the top and
     the number on the section can never read as two different tallies of the same
     cards (the old 5 cross-cutting tiles — overdue, in-progress … — counted across
     the buckets, so "In progress 5" never matched "Doing now 3"). The overdue /
     blocked / due-this-week signals still lead the written sentence above, where
     they read as narrative, not as a count that must reconcile with a section. */
  const BUCKET_TONE = {
    first: 'var(--flag)',
    doing: 'var(--s-inprogress)',
    next: 'var(--s-todo)',
  } as const;
  const buckets: [key: keyof typeof BUCKET_TONE, label: string, count: number][] = [
    ['first', 'Needs you first', work.needsFirst.length],
    ['doing', 'Doing now', work.doingNow.length],
    ['next', 'Up next', work.upNext.length],
  ];
  /* The make-up under each tile (#3). Each sub-line is counted FROM ITS OWN BUCKET,
     not from the cross-cutting glance — otherwise the headline and its make-up are
     two different populations and contradict (an overdue in-progress card lives in
     "Needs you first", so counting it under "Doing now" made "Doing now 1 · 3 in
     progress" — 1 ≠ 3). Each signal appears under one bucket only: overdue/blocked
     under what needs you first, in-progress/in-review under what's moving,
     due-this-week under what's up next. */
  const inBucket = (list: typeof work.needsFirst, pred: (m: typeof list[number]) => boolean) =>
    list.filter(pred).length;
  const tileSub: Record<keyof typeof BUCKET_TONE, string> = {
    first: signalLine(
      [
        [inBucket(work.needsFirst, (m) => m.overdue), 'overdue'],
        [inBucket(work.needsFirst, (m) => m.blocker !== ''), 'blocked'],
      ],
      'nothing flagged',
    ),
    doing: signalLine(
      [
        [inBucket(work.doingNow, (m) => m.issue.status === 'inprogress'), 'in progress'],
        [inBucket(work.doingNow, (m) => m.issue.status === 'inreview'), 'in review'],
      ],
      'nothing in flight',
    ),
    next: signalLine(
      [[inBucket(work.upNext, (m) => m.dueDays !== null && m.dueDays >= 0 && m.dueDays <= 7), 'due this week']],
      'none due this week',
    ),
  };

  return (
    <div className="mwx cap-fill">
      {/* The masthead: the day stated in words with the glance woven in, and the
          two-tab switch. One tab at a time keeps the screen calm; the Inbox tab
          carries the unread needs-action count so nothing waiting on me hides. */}
      <div className="mwx-mast">
        {/* The day and the two-tab switch share ONE fixed-height row, so the tabs
            keep their exact place when you change tabs. The Inbox-only situation
            line used to sit beside the day inside the flex row, which made the
            masthead taller on Inbox and re-centred the pills — the tabs appeared
            to jump as you toggled (Vinnie, 21 Oct). It now reads BELOW the row, so
            it never moves the switch. The top bar already titles the page "My
            work", so the masthead does not repeat it (#3). */}
        <div className="mwx-mast-row">
          <p className="mwx-day">{wordedDay(now)}</p>
          <div className="mwx-tabs" role="tablist" aria-label="My work">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'todo'}
              className={`mwx-tab${tab === 'todo' ? ' on' : ''}`}
              onClick={() => setTab('todo')}
            >
              To&nbsp;do <span className="n">{work.total}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'inbox'}
              className={`mwx-tab${tab === 'inbox' ? ' on' : ''}`}
              onClick={() => setTab('inbox')}
            >
              Inbox {needsActionCount > 0 && <span className="n crit">{needsActionCount}</span>}
            </button>
          </div>
        </div>
        {tab === 'inbox' && <p className="mwx-sit">{inboxSit}</p>}
      </div>

      {/* The tab panel fades in on each switch (the app's standard view-in motion),
          so toggling reads as a soft change rather than a hard swap. Keyed on the
          tab so the animation re-runs each time. */}
      <div className="mwx-panel" key={tab}>
      {tab === 'todo' ? (
        <>
          {/* Self-explaining tiles (#3, option C): one tile per section below, in
              the section's own colour (#4), and under each count the signal that
              composes it — "1 overdue · 2 blocked", "3 in progress · 1 in review",
              "6 due this week". Each signal sits under exactly one bucket, so the
              headline and its make-up read together and nothing is restated. The
              separate detail line is gone (its numbers now live in the sub-lines);
              the day states itself once, up in the masthead. */}
          <div className="mwx-glance">
            {buckets.map(([key, label, n]) => (
              <div className="mwx-stat" key={key} style={{ ['--tone' as string]: BUCKET_TONE[key] }}>
                <span className="v">{n}</span>
                <span className="l">
                  <span className="d" style={{ background: BUCKET_TONE[key] }} />
                  {label}
                </span>
                <span className="s">{tileSub[key]}</span>
              </div>
            ))}
          </div>

          {toDoEmpty ? (
            <div className="mwx-empty">
              {me
                ? 'You’re all caught up — nothing is assigned to you right now across the boards you can see.'
                : 'Sign in to see the work assigned to you.'}
            </div>
          ) : (
            <div className="a-body">
              <div className="a-agenda">
                {agendaSection(
                  'Needs you first',
                  'overdue or blocked',
                  <>{work.needsFirst.map(agendaRow)}</>,
                  work.needsFirst.length,
                  true,
                  BUCKET_TONE.first
                )}
                {agendaSection(
                  'Doing now',
                  'in progress & in review',
                  <>{work.doingNow.map(agendaRow)}</>,
                  work.doingNow.length,
                  false,
                  BUCKET_TONE.doing
                )}
                {agendaSection(
                  'Up next',
                  'assigned, not started',
                  <>
                    {upNextShown.map(agendaRow)}
                    {work.upNext.length > UP_NEXT_PEEK && (
                      <button type="button" className="amore" onClick={() => setShowAllUpNext((v) => !v)}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showAllUpNext ? 'rotate(90deg)' : 'none' }}>
                          <path d="M7 5l5 5-5 5" />
                        </svg>
                        {showAllUpNext ? 'Show fewer' : `Show all ${work.upNext.length} — the rest of what’s assigned to you`}
                      </button>
                    )}
                  </>,
                  work.upNext.length,
                  false,
                  BUCKET_TONE.next
                )}
                {/* I'M ACCOUNTABLE FOR — the work I OWN that someone else is doing
                    (two-part assignee, A). Shown only when there is any, below my
                    own doing-list, so "what I do" and "what I answer for" read
                    apart. One combined list; these are not my hands-on queue. */}
                {accountable.total > 0 &&
                  agendaSection(
                    'I’m accountable for',
                    'owned by you — someone else is doing these',
                    <>{[...accountable.needsFirst, ...accountable.doingNow, ...accountable.upNext].map(agendaRow)}</>,
                    accountable.total,
                    false,
                    undefined,
                  )}
                {loading && work.total === 0 ? <div className="mwx-empty">Reading your work…</div> : null}
              </div>

              <aside className="a-ledger">
                <div className="led-block">
                  <div className="led-h">
                    <span className="led-t">My epics</span>
                    <span className="led-n">{myEpics.length}</span>
                    <span className="led-note">you own or work in</span>
                  </div>
                  {myEpics.length === 0 ? (
                    <div className="led-empty">You don’t own or have a card in an epic yet.</div>
                  ) : (
                    myEpics.map((e) => (
                      <div className="erow" key={e.name}>
                        <span className="erow-sw" style={{ background: e.colour }} aria-hidden="true" />
                        <span className="erow-nm" title={e.name}>
                          {e.name}
                          {e.own && <span className="erow-own" title="You own this epic">own</span>}
                        </span>
                        <span className="erow-r">
                          <span className="erow-bar"><i style={{ width: `${e.progressPct}%` }} /></span>
                          <span className="erow-pct">{e.progressPct}%</span>
                          <span className={`rag ${HEALTH_CLS[e.health]}`}><span className="dot" />{HEALTH_WORD[e.health]}</span>
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {heavyWeek ? (
                  <div className="led-block">
                    <div className="led-h">
                      <span className="led-t">Blocked, by reason</span>
                      <span className="led-n">{work.glance.blocked}</span>
                      <span className="led-note">what to chase</span>
                    </div>
                    {blockedByReason.map((b) => {
                      const open = openReason === b.reason;
                      return (
                        <div className="brow-wrap" key={b.reason}>
                          <button
                            type="button"
                            className={`brow${open ? ' on' : ''}`}
                            aria-expanded={open}
                            onClick={() => setOpenReason((r) => (r === b.reason ? null : b.reason))}
                          >
                            <span className="brow-n">{b.n}</span>
                            <span className="brow-r">{b.reason}</span>
                            <svg className="brow-chev" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M7 5l5 5-5 5" />
                            </svg>
                          </button>
                          <Expander open={open}>
                            <div className="brow-cards">
                              {b.cards.map((m) => (
                                <button
                                  type="button"
                                  key={`${m.boardId}:${m.issue.id}`}
                                  className="brow-card"
                                  onClick={() => openCardFromRow(m)}
                                >
                                  <span className="brow-card-ref">{m.issue.ref}</span>
                                  <span className="brow-card-t" title={m.issue.title} aria-label={m.issue.title}>{m.issue.title}</span>
                                  <span className="brow-card-bd">{m.boardName}</span>
                                </button>
                              ))}
                            </div>
                          </Expander>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="led-block">
                    <div className="led-h">
                      <span className="led-t">My areas</span>
                      <span className="led-n">{myAreas.length}</span>
                      <span className="led-note">where your work sits</span>
                    </div>
                    {myAreas.length === 0 ? (
                      <div className="led-empty">None of your cards has an area yet.</div>
                    ) : (
                      myAreas.map((a) => (
                        <div className="erow" key={a.name}>
                          <span className="erow-sw" style={{ background: a.colour || 'var(--t3)' }} aria-hidden="true" />
                          <span className="erow-nm" title={a.name}>{a.name}</span>
                          <span className="erow-r">
                            <span className="erow-bar"><i style={{ width: `${a.total ? Math.round((a.done / a.total) * 100) : 0}%` }} /></span>
                            <span className="erow-pct">{a.open} open</span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </aside>
            </div>
          )}

          <p className="hint mwx-foot">
            Your cards, epics and areas from every board you can see — a read over data the Portfolio already loads.
            Opening anything switches to its board.
          </p>
        </>
      ) : (
        <>
          {notifications.length === 0 ? (
            <div className="mwx-empty">
              {me
                ? 'You’re all caught up — nothing has been sent to you across the boards you can see.'
                : 'Sign in to see what has been sent to you.'}
            </div>
          ) : (
            <div className="a-inbox">
              {agendaSection(
                'Needs you first',
                'what is waiting on you to act',
                needsAction.length === 0 ? (
                  <p className="asec-empty">Nothing is waiting on you.</p>
                ) : (
                  <div className="mw-ib-rows">{needsAction.map((n) => inboxRow(n))}</div>
                ),
                needsAction.length,
                true
              )}
              {agendaSection(
                'FYI',
                'decisions & updates on your work',
                fyi.length === 0 ? (
                  <p className="asec-empty">Nothing here yet.</p>
                ) : (
                  <div className="mw-ib-rows">{fyi.map((n) => inboxRow(n))}</div>
                ),
                fyi.length
              )}
              {/* #1 — the Done reveal: call-outs you have dealt with, kept in reach
                  without cluttering the live inbox. Folded away until asked for. */}
              {done.length > 0 && (
                <div className="mw-ib-done-group">
                  <button
                    type="button"
                    className={`mw-ib-done-toggle${showDone ? ' on' : ''}`}
                    aria-expanded={showDone}
                    onClick={() => setShowDone((v) => !v)}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 5l5 5-5 5" />
                    </svg>
                    {showDone ? 'Hide done' : `Done (${done.length})`}
                  </button>
                  <Expander open={showDone}><div className="mw-ib-rows">{done.map((n) => inboxRow(n, true))}</div></Expander>
                </div>
              )}
            </div>
          )}

          <p className="hint mwx-foot">
            Everything sent to you, across every board. Sign or reply here, or open the card — then mark it done.
            This is the home of the bell; Activity stays the per-board log of everyone’s events.
          </p>
        </>
      )}
      </div>

      {inlineCardId && inlineCardBoard && (
        <div className="overlay mw-scene" onClick={closePanel}>
          <MyWorkCard
            boardId={inlineCardBoard}
            boardName={panelBoardName}
            leaving={panelLeaving}
            onClose={closePanel}
            onOpenFull={() => openFullCard(inlineCardBoard, inlineCardId)}
          />
        </div>
      )}
    </div>
  );
}
